import { Box, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from 'recharts';

export type DailyCountDatum = {
  range: string;
  value: number;
};

const BAR_COLOR_DEFAULT = '#DCDCDC';
const BAR_COLOR_PEAK = '#FACC15';

/** 랜덤 광고/영상 광고 탭: 1~3순위 바 색상 */
const RANK_COLORS = ['#FBBF24', '#FCD34D', '#FDE68A'] as const;

/** colorScale="gradient": 값이 클수록 진해지는 그라데이션 (연함 -> 프로젝트 테마 파란색 theme.palette.primary.main #0044D2) */
const GRADIENT_LIGHT = { r: 0xe6, g: 0xec, b: 0xfb };
const GRADIENT_DARK = { r: 0x00, g: 0x44, b: 0xd2 };

function toHex(n: number): string {
  return Math.round(n).toString(16).padStart(2, '0');
}

function interpolateGradientColor(ratio: number): string {
  const r = GRADIENT_LIGHT.r + (GRADIENT_DARK.r - GRADIENT_LIGHT.r) * ratio;
  const g = GRADIENT_LIGHT.g + (GRADIENT_DARK.g - GRADIENT_LIGHT.g) * ratio;
  const b = GRADIENT_LIGHT.b + (GRADIENT_DARK.b - GRADIENT_LIGHT.b) * ratio;
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

type Props = {
  data?: DailyCountDatum[];
  /** true면 1~3순위 바에 RANK_COLORS 적용 (랜덤 광고, 영상 광고 탭) */
  highlightTop3?: boolean;
  /** 'gradient'면 값 크기에 비례한 인디고 그라데이션 적용 (highlightTop3보다 우선) */
  colorScale?: 'default' | 'gradient';
  title?: string;
};

export default function RouletteContentDailyCountChart({
  data,
  highlightTop3 = false,
  colorScale = 'default',
  title = '일 참여 횟수 현황',
}: Props) {
  const chartData = data ?? [];
  const maxValue = chartData.length ? Math.max(...chartData.map((d) => d.value), 1) : 1;
  const minValue = chartData.length ? Math.min(...chartData.map((d) => d.value)) : 0;

  const getBarColor = (range: string) => {
    if (colorScale === 'gradient') {
      const value = chartData.find((d) => d.range === range)?.value ?? 0;
      const ratio = maxValue > minValue ? (value - minValue) / (maxValue - minValue) : 1;
      return interpolateGradientColor(ratio);
    }
    if (highlightTop3) {
      const sorted = [...chartData].sort((a, b) => b.value - a.value);
      const rank = sorted.findIndex((d) => d.range === range);
      if (rank >= 0 && rank < 3) return RANK_COLORS[rank];
      return BAR_COLOR_DEFAULT;
    }
    const peakRange =
      chartData.length > 0
        ? chartData.reduce((max, d) => (d.value > max.value ? d : max), chartData[0]).range
        : '';
    return range === peakRange ? BAR_COLOR_PEAK : BAR_COLOR_DEFAULT;
  };

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: 3,
        p: 2,
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight={600}
        mb={1.5}
        color="#475569"
        textAlign="center"
        sx={{ fontSize: '0.95rem' }}
      >
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData} margin={{ top: 20, right: 16, left: 4, bottom: 4 }}>
          <XAxis
            dataKey="range"
            tick={{ fontSize: 9 }}
            axisLine={{ stroke: '#e0e0e0' }}
            tickLine={false}
            interval={0}
          />
          <YAxis hide domain={[0, maxValue * 1.1]} />
          <Bar
            dataKey="value"
            radius={[4, 4, 0, 0]}
            maxBarSize={28}
            isAnimationActive={false}
            label={{
              position: 'top',
              fontSize: 9,
              fill: '#000',
              formatter: (v: number) => v.toLocaleString('ko-KR'),
            }}
          >
            {chartData.map((entry) => (
              <Cell key={entry.range} fill={getBarColor(entry.range)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
